import {
    type FeedingDashboardConfig,
    type FeedingPen,
    type PenWarning,
    type PenWarningTrigger,
    type WarningMetric,
    type WarningTarget,
    type WarningTier,
    warningTargetOf,
    warningTier,
} from '@/domain/feedingDashboard';

/**
 * The target a KPI value is judged against. Targets are configured per pen, so several
 * pens share one only when none of them overrides it differently; otherwise the card
 * shows the range, and a range keeps each pen's own target so the value can be judged
 * against all of them.
 */
export type KpiTarget =
    | { kind: 'shared'; target: WarningTarget }
    | { kind: 'range'; min: number; max: number; targets: WarningTarget[] };

/**
 * One pen uses its own resolved target; several a shared one, or a range. With a shared
 * target and differing caution margins the widest margin applies, so the second tier is
 * reached only where it would be reached for every pen.
 */
export function resolveKpiTarget(
    pens: FeedingPen[],
    site: FeedingDashboardConfig,
    metric: WarningMetric
): KpiTarget {
    if (pens.length === 0) return { kind: 'shared', target: warningTargetOf(site, metric) };
    const targets = pens.map((pen) => warningTargetOf(pen.config, metric));
    const values = targets.map((entry) => entry.target);
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min !== max) return { kind: 'range', min, max, targets };
    return {
        kind: 'shared',
        target: { target: min, warnPct: Math.max(...targets.map((entry) => entry.warnPct)) },
    };
}

const METRIC: Record<PenWarningTrigger, WarningMetric> = {
    fcrSystem: 'fcr',
    fcrBioscope: 'fcr',
    rgiSystem: 'rgi',
    rgiBioscope: 'rgi',
};

/** The mildest of the tiers: the value is only as far off as it is for every pen. */
function mildest(tiers: WarningTier[]): WarningTier {
    if (tiers.includes('none')) return 'none';
    return tiers.includes('warning') ? 'warning' : 'critical';
}

/**
 * Judges a value against every contributing pen's target and keeps the mildest tier, so
 * a mean warns, and reaches the second tier, only where it would for each of those pens:
 * an RGI below the lowest target, a bFCR above the highest.
 *
 * The condition names only a target and margin that grade the value to that tier on
 * their own: the stricter pen nearest the value can put it past its margin while a pen
 * with a wider margin holds the tier at the first, and naming the stricter one would
 * contradict the tier shown. Of the pens at the mildest tier it names the target nearest
 * the value, with the widest margin set on it; the value is past every pen's target.
 */
function rangeWarning(
    metric: WarningMetric,
    value: number,
    targets: WarningTarget[]
): { tier: WarningTier; target: WarningTarget } {
    const graded = targets.map((entry) => ({ entry, tier: warningTier(metric, value, entry) }));
    const tier = mildest(graded.map((entry) => entry.tier));
    const setting = graded.filter((entry) => entry.tier === tier).map((entry) => entry.entry);
    const values = setting.map((entry) => entry.target);
    const nearest = metric === 'fcr' ? Math.max(...values) : Math.min(...values);
    const warnPct = Math.max(
        ...setting.filter((entry) => entry.target === nearest).map((entry) => entry.warnPct)
    );
    return { tier, target: { target: nearest, warnPct } };
}

/**
 * The shared warning rule applied to a KPI value: the same four triggers and tiers as a
 * pen row. The value is compared as the API delivers it, which is how the card shows it.
 * A Bioscope value only exists here when ready pens produced it, so an unavailable
 * Bioscope metric never triggers.
 */
export function kpiWarning(
    trigger: PenWarningTrigger,
    value: number | null,
    target: KpiTarget
): PenWarning | null {
    if (value == null) return null;
    const metric = METRIC[trigger];
    const judged =
        target.kind === 'shared'
            ? { tier: warningTier(metric, value, target.target), target: target.target }
            : rangeWarning(metric, value, target.targets);
    if (judged.tier === 'none') return null;
    return {
        trigger,
        metric,
        value,
        target: judged.target.target,
        warnPct: judged.target.warnPct,
        tier: judged.tier,
    };
}

/** The selected pens whose ids are in `ids`, in selection order. */
export function pensIn(pens: FeedingPen[], ids: number[]): FeedingPen[] {
    const wanted = new Set(ids);
    return pens.filter((pen) => wanted.has(pen.cageId));
}

/**
 * The target a card's Target row names: that of the pens behind its values, so a pen that
 * contributes to none of them cannot turn the target that explains a warning into a
 * range. A card with no value names the whole selection's.
 */
export function footerTarget(
    pens: FeedingPen[],
    contributors: number[][],
    site: FeedingDashboardConfig,
    metric: WarningMetric
): KpiTarget {
    const behind = pensIn(pens, contributors.flat());
    return resolveKpiTarget(behind.length > 0 ? behind : pens, site, metric);
}
